<template>
    <div :class="inModal ? null : 'rounded p-3 p-md-5 bg-light'">

        <div class="text-center">

            <h1 class="mb-4">Your Aesthetic Profile</h1>

            <div v-if="profile.isOwner" class="clickable text-primary mb-5" @click="editName=true" style="text-decoration: underline dotted; text-decoration-thickness: 0.05rem; text-underline-offset: 4px;">
                <h4>{{ profile.name }}</h4>
            </div>
            <div v-else class="text-primary mb-5">
                <h4>{{ profile.name }}</h4>
            </div>
            
            <template v-if="profile.description">
                <div class="position-relative">
                    <h6 class="text-center overflow-hidden" style="transition: max-height 0.5s;" :class="showFullDescription ? 'mb-5 max-vh-100 overflow-auto' : 'max-vh-20 overflow-hidden'">
                        {{ profile.description }}
                    </h6>
                    <div v-if="!showFullDescription" class="position-absolute w-100 bottom-0 start-0 end-0 h-40" style="background: linear-gradient(180deg, rgba(3,46,60,0) 0%, rgba(3,46,60,1) 100%);">&nbsp;</div>
                </div>
                <div v-if="!showFullDescription" class="text-center mb-5">
                    <ui-button :underline="true" size="sm" @click="showFullDescription=true">
                        read more
                    </ui-button>
                </div>
            </template>

            <container class="mb-4">
                <row>
                    <column v-if="style" :md="8">
                        <div class="x-small text-muted text-uppercase spaced-letters">Styles:</div>
                        <p class="text-primary">{{ style }}</p>
                    </column>
                    <column v-if="color" :md="8">
                        <div class="x-small text-muted text-uppercase spaced-letters">Colors:</div>
                        <p class="text-primary">{{ color }}</p>
                    </column>
                    <column v-if="era" :md="8">
                        <div class="x-small text-muted text-uppercase spaced-letters">Decor:</div>
                        <p class="text-primary">{{ era }}</p>
                    </column>
                </row>
            </container>

            <template v-if="profile.verified">
                <ui-button v-if="profile.hasUser" class="me-0 me-md-3 mb-3 mb-md-0" to="/travelers" color="secondary" :outline="true" size="sm" block="sm">
                    All Aesthetic Profiles
                </ui-button>
                <ui-button v-else class="me-0 me-md-3 mb-3 mb-md-0" color="secondary" :outline="true" :disabled="buttonsLoading" :loading="buttonsLoading" size="sm" block="sm" @click="openLoginForm">
                    Assign to Your Account
                </ui-button>
                <ui-button v-if="profile.shareHash" @click="sharing=true" color="secondary" :outline="true" size="sm" block="sm">
                    Share
                    <share-icon class="ms-2" />
                </ui-button>
            </template>
            
        </div>

        
        <share-modal v-if="sharing" @close="sharing=false" :link="shareLink" title="Share Aesthetic Profile" :headline="profile.defaultName" snippet="This is my style profile, what's yours?">
            <ai-profile-share-card :profile="profile" class="mb-3" />
        </share-modal>

        <edit-ai-profile-name-modal v-if="editName" @close="editName=false" :profile="profile" @saved="$emit('refresh')" />

        <login-signup-modal v-if="showLoginForm" :open="loginFormOpen" @close="loginFormClosed" @userAccount="setUserId" :quickSignup="true" signupType="traveler" />
    </div>
</template>

<script type="text/javascript">
    import EditAiProfileNameModal from '@/components/Content/Travelers/AI/EditNameModal.vue';
    import AiProfileShareCard from '@/components/Content/Travelers/AI/Cards/Share.vue';
    import ShareModal from '@/components/Content/ShareModal.vue';
    import ShareIcon from '@/assets/icons/share.svg.vue';
    import LoginSignupModal from '@/components/Content/Auth/LoginSignupModal.vue';
    import CommonComponents from '@/components/Common.vue';

    import ApiObject from '@/classes/ApiObject.js';
    import Api from '@/classes/Api.js';

    import { UseAuthStore } from '@/store/Auth.js';
    import { UseUIStore } from '@/store/UI.js';

    export default {
        components: {
            ShareModal,
            ShareIcon,
            AiProfileShareCard,
            EditAiProfileNameModal,
            LoginSignupModal,
            ...CommonComponents,
        },

        setup() {
            return {
                authStore: UseAuthStore(),
                uiStore: UseUIStore(),
            };
        },

        data() {
            return {
                sharing: false,
                editName: false,

                showLoginForm: false,
                loginFormOpen: true,

                isAssigningToAccount: false,
                loggedInUserId: null,
                buttonsLoading: false,

                showFullDescription: false,
            };
        },

        props: {
            profile: {
                type: ApiObject,
                required: true,
            },

            inModal: {
                type: Boolean,
                default: true,
            },
        },

        created() {
            if (this.authStore.loggedInUser) {
                this.loggedInUserId = this.authStore.loggedInUser.id;
            }
        },

        watch: {
            loggedInUserId() {
                if ((this.loggedInUserId) && (this.isAssigningToAccount)) {
                    this.assignProfile();
                }
            },
        },

        computed: {
            style() {
                return this.styleByType('style');
            },

            color() {
                return this.styleByType('color theme');
            },

            era() {
                return this.styleByType('era');
            },

            shareLink() {
                return `${location.origin}/aesthetic-profile/${this.profile.id}/share/${this.profile.shareHash}`;
            },
        },

        methods: {

            setUserId() {
                if (this.authStore.loggedInUser) {
                    this.loggedInUserId = this.authStore.loggedInUser.id;
                }
                else {
                    this.loggedInUserId = null;
                }
            },

            styleByType(type) {
                let re = [];
                if (
                    (this.profile) &&
                    (this.profile.styles) &&
                    (this.profile.styles.length)
                ) {
                    for (let i=0; i<this.profile.styles.length; i++) {
                        if ((this.profile.styles[i].group == 'overall') && (this.profile.styles[i].type == type)) {
                            re.push(this.profile.styles[i].value);
                            if (re.length >= 3) break;
                        }
                    }
                }

                if (re.length) return re.join(', ');
                return null;
            },

            openLoginForm() {
                if (this.loggedInUserId) {
                    this.assignProfile();
                }
                else {
                    this.isAssigningToAccount = true;
                    this.loginFormOpen = true;
                    this.showLoginForm = true;
                }
            },

            loginFormClosed() {
                this.showLoginForm = false;
                this.loginFormOpen = true;
            },

            async assignProfile() {
                if (this.buttonsLoading) return;

                this.buttonsLoading = true;
                this.isAssigningToAccount = false;

                let response = await Api.post(`travelers/ai/profile/${this.profile.id}/assign`);

                if ((response.valid) && (response.results) && (!response.results.error)) {
                    this.uiStore.toast({
                        success: true,
                        title: 'Profile Assigned',
                        message: 'This profile was assigned to your account. To view all your profiles, click on the "All Aesthetic Profiles"',
                    });

                    this.$emit('refresh');
                }
                else {
                    let error = 'Could not complete request';
                    if ((response.results) && (response.results.error)) {
                        if (response.results.error.message) error = response.results.error.message;
                        else error = response.results.error;
                    }

                    this.uiStore.toast({
                        error: true,
                        title: 'Error',
                        message: error,
                    });

                    this.submitError = true;
                }

                this.buttonsLoading = false;
            },
        },
    };

</script>
